/**
 * VideoQAReview -- Dedicated review page for video edit QA.
 *
 * Flow:
 * 1. Upload a video (drag & drop or file picker) OR select from recent videos
 * 2. Video gets transcribed + analyzed via existing pipeline
 * 3. User selects preset / options via QAOptionsPanel
 * 4. QA check runs with selected options
 * 5. User reviews findings with video player + transcript context, grouped by category
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, TextField, CircularProgress,
  Alert, LinearProgress, Tooltip, InputAdornment, Stepper, Step, StepLabel,
  Collapse, IconButton, Select, MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import BugReportIcon from '@mui/icons-material/BugReport';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import PageHeader from '../../ui-components/PageHeader';
import QAOptionsPanel from './QAOptionsPanel';
import {
  FINDING_TYPE_META, SEVERITY_META, ACTION_LABELS, CATEGORY_META, PRESETS,
} from './qaConstants';
import FeedbackCheckDialog from './FeedbackCheckDialog';
import FeedbackCheckResultaat from './FeedbackCheckResultaat';

const API = `${AI_ENGINE_URL}/api/v1/video-search`;

/**
 * Format a raw transcript into readable paragraphs (client-side).
 * Groups sentences (~4 per paragraph) and preserves existing paragraph breaks.
 */
function formatTranscriptForDisplay(raw) {
  if (!raw) return '';
  // If already has paragraph breaks, use them
  if (raw.includes('\n\n')) return raw;
  // Split into sentences and group into paragraphs of ~4 sentences
  const sentences = raw.split(/(?<=[.!?])\s+/);
  const paragraphs = [];
  let current = [];
  for (const sent of sentences) {
    // Start a new paragraph at speaker labels (e.g. "Spreker 1:", "Jan:")
    if (/^[A-Z][a-zA-Z\s]*\d*\s*:/.test(sent) && current.length > 0) {
      paragraphs.push(current.join(' '));
      current = [];
    }
    current.push(sent);
    if (current.length >= 4) {
      paragraphs.push(current.join(' '));
      current = [];
    }
  }
  if (current.length) paragraphs.push(current.join(' '));
  return paragraphs.join('\n\n');
}
const BACKEND_API = BACKEND_URL;

const formatTime = (s) => {
  if (!s && s !== 0) return '';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const ALLOWED_TYPES = ['.mp4', '.mov', '.mkv', '.avi', '.webm'];

const PROCESS_STEPS = [
  { label: 'Uploaden', desc: 'Video wordt verstuurd naar de server' },
  { label: 'Audio extractie', desc: 'Audio wordt uit de video gehaald' },
  { label: 'Transcriberen', desc: 'Spraak wordt omgezet naar tekst' },
  { label: 'Opslaan', desc: 'Keyframes opslaan — de beeldbeschrijving volgt op de achtergrond' },
  { label: 'QA Check', desc: 'Controleren op editfouten' },
];

const STATUS_LABELS = {
  completed: { label: 'Afgerond', color: '#10b981', bg: '#ecfdf5' },
  not_run: { label: 'Niet geselecteerd', color: '#94a3b8', bg: '#f1f5f9' },
  not_implemented: { label: 'Binnenkort beschikbaar', color: '#d97706', bg: '#fffbeb' },
};

export default function VideoQAReview() {
  const { instance } = useMsal();
  const userEmail = instance.getActiveAccount()?.username || '';
  const [searchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const videoIdParam = searchParams.get('videoId');

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [qaResult, setQaResult] = useState(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState(null);
  const [qaElapsed, setQaElapsed] = useState(0);
  const [transcript, setTranscript] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [formattedTranscript, setFormattedTranscript] = useState(null);
  const [formattingTranscript, setFormattingTranscript] = useState(false);
  const [qaOptions, setQaOptions] = useState(null);

  // Persistent review state
  const [currentReviewId, setCurrentReviewId] = useState(null);
  const [qaFindings, setQaFindings] = useState([]); // findings with DB status
  const [qaRunId, setQaRunId] = useState(null); // unique run ID for polling
  const [qaSource, setQaSource] = useState(null); // "azure", "worker", "azure_fallback"

  // State for showing options panel after upload/select but before QA
  const [pendingVideo, setPendingVideo] = useState(null);
  const [pendingProjectData, setPendingProjectData] = useState(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState(0); // 0-4 stepper
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Jump video to timestamp when finding is clicked
  const seekToTimestamp = useCallback((seconds) => {
    if (videoRef.current && seconds >= 0) {
      videoRef.current.currentTime = seconds;
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      videoRef.current.play().catch(() => {});
    }
  }, []);

  // Frame.io URL state
  const [frameioUrl, setFrameioUrl] = useState('');
  const [frameioLoading, setFrameioLoading] = useState(false);
  const [frameioStep, setFrameioStep] = useState(0);

  // Push to Frame.io state
  const [pushingToFrameio, setPushingToFrameio] = useState(false);
  const [pushToFrameioResult, setPushToFrameioResult] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false); // feedbackcontrole-dialoog

  // Elapsed timer for QA loading
  useEffect(() => {
    if (!qaLoading) { setQaElapsed(0); return; }
    const start = Date.now();
    const iv = setInterval(() => setQaElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [qaLoading]);

  // Fetch project data when pendingVideo is set (for pre-filling context)
  useEffect(() => {
    if (!pendingVideo) { setPendingProjectData(null); return; }
    let cancelled = false;
    (async () => {
      try {
        // First: get video detail for category/variant
        const detailResp = await authFetch(`${API}/detail/${pendingVideo.id}`);
        let videoDetail = {};
        if (detailResp.ok) {
          videoDetail = await detailResp.json();
        }

        // Then: if we have a project_number, fetch project from Airtable sync
        let projectInfo = {};
        const projNum = pendingVideo.project_number || videoDetail.project_number;
        if (projNum) {
          try {
            const projResp = await authFetch(
              `${AI_ENGINE_URL}/api/v1/airtable/projects?search=${encodeURIComponent(projNum)}&limit=1`
            );
            if (projResp.ok) {
              const projData = await projResp.json();
              const match = (projData.projects || []).find(p =>
                p.project_number === projNum || p.fields?.Projectnummer === projNum
              );
              if (match) {
                projectInfo = {
                  project_type: match.project_type || match.fields?.Projecttype || match.fields?.['Project Type'] || '',
                  description: match.description || match.fields?.Omschrijving || '',
                  klant_name: match.klant_name || match.fields?.KlantnaamString || '',
                };
              }
            }
          } catch { /* non-critical */ }
        }

        if (!cancelled) {
          setPendingProjectData({
            project_type: projectInfo.project_type || '',
            description: projectInfo.description || '',
            klant_name: projectInfo.klant_name || '',
          });
          // Also enrich pendingVideo with detail data
          if (videoDetail.category || videoDetail.variant) {
            setPendingVideo(prev => prev ? {
              ...prev,
              category: prev.category || videoDetail.category || '',
              variant: prev.variant || videoDetail.variant || '',
              project_number: prev.project_number || videoDetail.project_number || '',
            } : prev);
          }
        }
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [pendingVideo?.id]);

  // QA history from database
  const [qaHistory, setQaHistory] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/qa/reviews?limit=20`);
      if (resp.ok) {
        const data = await resp.json();
        setQaHistory(data.reviews || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Deep link support: load review from URL param
  useEffect(() => {
    if (!reviewParam) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await authFetch(`${API}/qa/reviews/${reviewParam}`);
        if (resp.ok && !cancelled) {
          const data = await resp.json();
          const videoData = {
            id: data.video_id,
            filename: data.video_filename,
            duration: data.video_duration,
            player_url: data.video_player_url,
            review_url: data.video_review_url,
          };

          // Issue 6c: Hydrate video detail (get video_blob_url for player)
          try {
            const detailResp = await authFetch(`${API}/detail/${data.video_id}`);
            if (detailResp.ok && !cancelled) {
              const detail = await detailResp.json();
              videoData.video_blob_url = detail.video_blob_url || detail.upload_blob_url;
              videoData.thumb_small = detail.thumb_small;
              videoData.thumb_large = detail.thumb_large;
              videoData.client_name = detail.client_name || '';
              videoData.project_number = detail.project_number || data.project_number || '';
              videoData.frameio_asset_id = detail.frameio_asset_id || '';
              if (detail.transcript_raw) setTranscript(detail.transcript_raw);
            }
          } catch { /* non-critical */ }

          if (!cancelled) {
            setSelectedVideo(videoData);
            setQaResult(data.result_data);
            setQaFindings(data.findings || []);
            setCurrentReviewId(parseInt(reviewParam));
            setQaSource(data.source || null);

            // Issue 6b: Restore qaOptions from stored data
            const storedOptions = {};
            if (data.preset) storedOptions.preset = data.preset;
            if (data.strictness) storedOptions.strictness = data.strictness;
            if (data.checks_config) storedOptions.checks = data.checks_config;
            if (data.context_data) storedOptions.context = data.context_data;
            setQaOptions(Object.keys(storedOptions).length > 0 ? storedOptions : null);
          }
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [reviewParam]);

  // Deep link support: pre-select video from URL param ?videoId=xxx
  useEffect(() => {
    if (!videoIdParam || reviewParam) return; // reviewParam takes priority
    let cancelled = false;
    (async () => {
      try {
        const resp = await authFetch(`${API}/detail/${videoIdParam}`);
        if (resp.ok && !cancelled) {
          const detail = await resp.json();
          setPendingVideo({
            id: parseInt(videoIdParam),
            filename: detail.filename,
            duration: detail.duration,
            video_blob_url: detail.video_blob_url,
            project_number: detail.project_number,
            category: detail.category,
            variant: detail.variant,
          });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [videoIdParam, reviewParam]);

  const addToHistory = useCallback((video, findingsCount) => {
    // Refetch from DB to update history
    fetchHistory();
  }, [fetchHistory]);

  const handleFormatTranscript = async () => {
    if (!transcript || formattingTranscript) return;
    setFormattingTranscript(true);
    try {
      const resp = await authFetch(`${API}/qa/format-transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript, video_id: selectedVideo?.id }),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.formatted_transcript) {
          setFormattedTranscript(data.formatted_transcript);
        }
      }
    } catch { /* ignore — button simply won't update */ } finally {
      setFormattingTranscript(false);
    }
  };

  const runQA = useCallback(async (video, options) => {
    setQaLoading(true);
    setQaError(null);
    setQaResult(null);
    setQaOptions(options || null);
    setQaRunId(null);
    setQaSource(null);

    // Enrich video data from API (gets video_blob_url, duration, player_url, transcript etc.)
    let enriched = { ...video };
    setTranscript(null);
    setTranscriptOpen(false);
    setFormattedTranscript(null);
    try {
      const detailResp = await authFetch(`${API}/detail/${video.id}`);
      if (detailResp.ok) {
        const detail = await detailResp.json();
        enriched = {
          ...enriched,
          video_blob_url: enriched.video_blob_url || detail.video_blob_url || detail.upload_blob_url,
          duration: enriched.duration || detail.duration,
          player_url: enriched.player_url || detail.player_url,
          thumb_small: enriched.thumb_small || detail.thumb_small,
          thumb_large: enriched.thumb_large || detail.thumb_large,
          client_name: enriched.client_name || detail.client_name || '',
          project_number: enriched.project_number || detail.project_number || '',
          category: enriched.category || detail.category || '',
          variant: enriched.variant || detail.variant || '',
          frameio_asset_id: enriched.frameio_asset_id || detail.frameio_asset_id || '',
        };
        if (detail.transcript_raw) setTranscript(detail.transcript_raw);
      }
    } catch (e) { /* non-critical, continue with existing data */ }

    setSelectedVideo(enriched);

    try {
      // Start QA (async, returns immediately) — send options as JSON body
      const fetchOpts = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options || {}),
      };
      const createdByParam = userEmail ? `?created_by=${encodeURIComponent(userEmail)}` : '';
      const startResp = await authFetch(`${API}/qa/${enriched.id}/run${createdByParam}`, fetchOpts);
      if (!startResp.ok) {
        const err = await startResp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${startResp.status}`);
      }
      const startData = await startResp.json();
      const runId = startData.qa_run_id;
      setQaRunId(runId);

      // Poll for QA results (use run_id if available for per-run lookup)
      const runIdParam = runId ? `?run_id=${encodeURIComponent(runId)}` : '';
      const result = await new Promise((resolve, reject) => {
        let attempts = 0;
        async function pollQA() {
          attempts++;
          try {
            const resp = await authFetch(`${API}/qa/${enriched.id}/status${runIdParam}`);
            if (resp.ok) {
              const data = await resp.json();
              if (data.status === 'completed') { resolve(data); return; }
              if (data.status === 'failed') { reject(new Error(data.error || 'QA check mislukt')); return; }
            }
          } catch (e) { /* ignore poll errors */ }
          if (attempts > 300) { reject(new Error('QA check duurde te lang (>20 min)')); return; }
          setTimeout(pollQA, 4000);
        }
        setTimeout(pollQA, 3000);
      });

      setQaResult(result);
      setQaSource(result.source || result.meta?.source || null);
      // Update selectedVideo with data from QA result (includes player_url etc.)
      if (result.video) {
        setSelectedVideo((prev) => ({ ...prev, ...result.video }));
      }
      // Store review_id and load findings from DB if available
      if (result.review_id) {
        setCurrentReviewId(result.review_id);
        try {
          const reviewResp = await authFetch(`${API}/qa/reviews/${result.review_id}`);
          if (reviewResp.ok) {
            const reviewData = await reviewResp.json();
            setQaFindings(reviewData.findings || []);
          }
        } catch { /* ignore, use findings from result */ }
      }
      addToHistory(enriched, result.summary?.total || 0);
    } catch (e) {
      setQaError(e.message);
    } finally {
      setQaLoading(false);
    }
  }, [addToHistory]);

  // -- Upload + analyze + QA in one flow --------------------------------------

  // -- Shared polling helper for both upload and Frame.io flows ----------------

  const pollForCompletion = useCallback(async (videoId, filename, setStep) => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const MAX_ATTEMPTS = 450; // 450 * 4s = 30 min max

      async function checkStatus() {
        attempts++;
        let detail = null;
        try {
          const detailResp = await authFetch(`${API}/detail/${videoId}`);
          if (detailResp.ok) {
            detail = await detailResp.json();
            if (detail.processing_status === 'completed' && detail.transcript_raw) {
              resolve(detail);
              return;
            }
            if (detail.processing_status === 'failed') {
              reject(new Error(detail.processing_error || 'Video analyse mislukt'));
              return;
            }
          }
        } catch (e) {
          // Ignore individual poll errors
        }

        // Stepper op de échte stap van de engine (detail.voortgang); zonder die
        // informatie (oudere engine, herstart) valt hij terug op de klok.
        if (setStep) {
          const stap = detail?.voortgang?.stap;
          const tijden = detail?.voortgang?.tijden || {};
          if (stap === 'audio+keyframes') setStep(tijden.audio ? 2 : 1);
          else if (stap && stap !== 'start') setStep(3);
          else if (attempts < 15) setStep(1);
          else if (attempts < 40) setStep(2);
          else setStep(3);
        }

        if (attempts >= MAX_ATTEMPTS) {
          reject(new Error('Verwerking duurde te lang (>30 min). Probeer een kortere video.'));
          return;
        }
        setTimeout(checkStatus, 4000);
      }

      setTimeout(checkStatus, 4000);
    });
  }, []);

  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_TYPES.includes(ext)) {
      setUploadError(`Bestandstype ${ext} niet ondersteund. Gebruik: ${ALLOWED_TYPES.join(', ')}`);
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadStep(0);

    try {
      setUploadStep(1); // Uploaden

      const formData = new FormData();
      formData.append('file', file);
      // QA-upload: transcript eerst, beeldbeschrijving en embedding daarna op de achtergrond.
      formData.append('doel', 'qa');

      // Step 1: Upload (fast, returns immediately)
      const resp = await authFetch(`${API}/upload-and-process-async`, {
        method: 'POST',
        body: formData,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Upload mislukt (${resp.status})`);
      }

      const result = await resp.json();
      const videoId = result.video_id;
      console.log(`[VideoQA] Uploaded: video_id=${videoId}, polling for completion...`);
      setUploadStep(2);

      // Step 2: Poll for processing completion
      await pollForCompletion(videoId, result.filename || file.name, setUploadStep);

      const video = {
        id: videoId,
        filename: result.filename || file.name,
        duration: result.duration,
        video_blob_url: result.blob_url,
        client_name: '',
        project_number: '',
      };

      setUploading(false);

      // Show options panel instead of running QA immediately
      setPendingVideo(video);
    } catch (e) {
      setUploadError(e.name === 'TypeError' && e.message === 'Failed to fetch'
        ? 'Kan geen verbinding maken met de server. Probeer de pagina te verversen.'
        : e.message);
      setUploading(false);
    }
  }, [pollForCompletion]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  // -- Frame.io URL handler ---------------------------------------------------

  // Frame.io flow: Phase 1 = quick metadata fetch → show options panel
  // Phase 2 (after "Start Analyse") = download + process + QA
  const [frameioMetadata, setFrameioMetadata] = useState(null); // {filename, filesize_mb, url}

  const handleFrameioSubmit = useCallback(async () => {
    const url = frameioUrl.trim();
    if (!url) return;

    setFrameioLoading(true);
    setUploadError(null);
    setQaError(null);

    try {
      console.log(`[VideoQA] Fetching Frame.io metadata for: ${url}`);

      // Phase 1: Quick metadata fetch only (~2s) — NO download yet
      const resp = await fetch(`${BACKEND_API}/Api/video-qa/from-frameio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Server fout (${resp.status})`);
      }

      const startResult = await resp.json();
      const filename = startResult.filename;
      console.log(`[VideoQA] Asset found: ${filename} (${startResult.filesize_mb} MB)`);

      // Show options panel immediately — download starts AFTER user clicks "Start Analyse"
      setFrameioMetadata({ filename, filesize_mb: startResult.filesize_mb, url });
      setFrameioUrl('');
      setFrameioLoading(false);
      setPendingVideo({
        id: null, // No video_id yet — will be resolved during processing
        filename,
        duration: 0,
        frameio_url: url,
        project_number: '', // Will be parsed from filename by QAOptionsPanel
      });
    } catch (e) {
      const msg = e.name === 'TypeError' && e.message === 'Failed to fetch'
        ? 'Kan geen verbinding maken met de server.'
        : e.message;
      setUploadError(msg);
      console.error('[VideoQA] Frame.io error:', e);
      setFrameioLoading(false);
    }
  }, [frameioUrl]);

  // Phase 2: After user clicks "Start Analyse" with Frame.io video (no video_id yet)
  const processFrameioAndRunQA = useCallback(async (video, options) => {
    if (!video.frameio_url || video.id) {
      // Normal flow — video already processed
      return runQA(video, options);
    }

    // Start the download + processing flow with stepper
    setFrameioLoading(true);
    setFrameioStep(1);
    setPendingVideo(null);

    const stepTimer = setInterval(() => {
      setFrameioStep((prev) => Math.min(prev + 1, 3));
    }, 20000);

    try {
      const filename = video.filename;
      console.log(`[VideoQA] Starting download + processing for: ${filename}`);
      setFrameioStep(1);

      // Poll AI Engine for the video (backend is downloading + uploading in background)
      let videoId = null;

      await new Promise((resolve, reject) => {
        let attempts = 0;
        const MAX_ATTEMPTS = 450;

        async function findVideo() {
          attempts++;
          try {
            const searchResp = await authFetch(`${API}/videos?search=${encodeURIComponent(filename)}&status=all&per_page=5`);
            if (searchResp.ok) {
              const data = await searchResp.json();
              const match = (data.videos || []).find(v => v.filename === filename);
              if (match) {
                videoId = match.id;
                console.log(`[VideoQA] Found video: id=${videoId}`);
                resolve();
                return;
              }
            }
          } catch (e) { /* ignore */ }

          if (attempts < 15) setFrameioStep(1);
          else if (attempts < 40) setFrameioStep(2);

          if (attempts >= MAX_ATTEMPTS) {
            reject(new Error('Video verscheen niet in AI Engine (>30 min). Backend download mogelijk mislukt.'));
            return;
          }
          setTimeout(findVideo, 4000);
        }
        setTimeout(findVideo, 4000);
      });

      // Poll for processing completion
      await pollForCompletion(videoId, filename, setFrameioStep);

      console.log(`[VideoQA] Processing complete, running QA for video ${videoId}...`);

      // Processing done — run QA with user's chosen options
      const enrichedVideo = { ...video, id: videoId };
      setFrameioLoading(false);
      setFrameioStep(0);
      clearInterval(stepTimer);
      await runQA(enrichedVideo, options);
    } catch (e) {
      clearInterval(stepTimer);
      const msg = e.name === 'AbortError'
        ? 'Verwerking duurde te lang. Probeer een kortere video.'
        : e.name === 'TypeError' && e.message === 'Failed to fetch'
          ? 'Kan geen verbinding maken met de server.'
          : e.message;
      setUploadError(msg);
      console.error('[VideoQA] Frame.io processing error:', e);
    } finally {
      setFrameioLoading(false);
      setFrameioStep(0);
    }
  }, [runQA, pollForCompletion]);

  // -- Handle options panel start (user clicks "Start Analyse")
  const handleOptionsStart = useCallback((options) => {
    if (!pendingVideo) return;
    const video = pendingVideo;
    setPendingVideo(null);
    // Frame.io video without id: needs download + processing first
    if (video.frameio_url && !video.id) {
      processFrameioAndRunQA(video, options);
    } else {
      runQA(video, options);
    }
  }, [pendingVideo, runQA, processFrameioAndRunQA]);

  // -- Finding status change handler ------------------------------------------
  const handleFindingStatusChange = useCallback(async (findingId, newStatus, { stil = false } = {}) => {
    const vorigeStatus = qaFindings.find((f) => f.id === findingId)?.finding_status || 'open';
    try {
      const resp = await authFetch(`${API}/qa/findings/${findingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ finding_status: newStatus, resolved_by: userEmail }),
      });
      if (resp.ok) {
        await resp.json();
        setQaFindings((prev) => prev.map((f) =>
          f.id === findingId ? { ...f, finding_status: newStatus } : f
        ));
        // Update review counters in current review id if available
        if (currentReviewId) {
          fetchHistory(); // Refresh history to show updated counters
        }
        if (!stil && vorigeStatus !== newStatus) {
          // Ongedaan maken = vorige status terugzetten (server + lokaal)
          biedOngedaanAan('Status bijgewerkt', () => handleFindingStatusChange(findingId, vorigeStatus, { stil: true }));
        }
      }
    } catch { /* ignore */ }
  }, [userEmail, currentReviewId, fetchHistory, qaFindings]);

  // -- Push findings to Frame.io as timestamped comments -----------------------
  const handlePushToFrameio = useCallback(async () => {
    if (!selectedVideo?.frameio_asset_id) return;
    setPushingToFrameio(true);
    setPushToFrameioResult(null);
    // Uitgesteld: 5 s "Annuleren" voordat de opmerkingen bij de klant in Frame.io staan.
    const r = await voerUitgesteldUit('Opmerkingen gaan naar Frame.io bij de klant…', async () => {
    try {
      const allFindings = qaFindings.length > 0 ? qaFindings : (qaResult?.findings || []);
      const formatted = allFindings.map(f => {
        const fType = f.finding_type || f.type;
        const typeMeta = FINDING_TYPE_META[fType] || { label: fType, icon: '?' };
        const sevMeta = SEVERITY_META[f.severity] || SEVERITY_META.medium;
        const conf = Math.round((f.confidence || 0) * 100);
        const action = ACTION_LABELS[f.suggested_action] || f.suggested_action || '';
        const lines = [
          `${typeMeta.icon} ${typeMeta.label} (${sevMeta.label}, ${conf}%)`,
          f.reason || '',
        ];
        if (f.evidence_text) lines.push('', `\ud83d\udcdd ${f.evidence_text}`);
        if (action) lines.push('', `\ud83d\udca1 Aanbeveling: ${action}`);
        return {
          text: lines.filter(Boolean).join('\n').trim(),
          timestamp: f.start_seconds || 0,
        };
      });

      const resp = await authFetch(`${BACKEND_API}/Api/video-qa/push-to-frameio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assetId: selectedVideo.frameio_asset_id,
          findings: formatted,
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data.commentsCreated > 0) {
          setPushToFrameioResult({
            success: true,
            message: `${data.commentsCreated}/${data.total} comments geplaatst in Frame.io`,
          });
        } else {
          // HTTP 200/207 maar 0 comments — alle Frame.io calls zijn gefaald
          const firstErr = data.errors?.[0];
          const detail = firstErr?.detail
            ? ` (${firstErr.status}: ${firstErr.detail.substring(0, 120)})`
            : '';
          const debug = data.debug
            ? ` [asset: ${data.debug.assetType}, resolved: ${data.debug.resolvedAssetId?.substring(0, 8)}...]`
            : '';
          setPushToFrameioResult({
            success: false,
            message: `Geen comments geplaatst — Frame.io API fout${detail}${debug}`,
          });
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        setPushToFrameioResult({
          success: false,
          message: err.error || `Fout (${resp.status})`,
        });
      }
    } catch (e) {
      setPushToFrameioResult({ success: false, message: e.message });
    } finally {
      setPushingToFrameio(false);
    }
    }, { duur: 5000 });
    if (r.geannuleerd) setPushingToFrameio(false);
  }, [selectedVideo, qaFindings, qaResult]);

  // -- Open a review from history (load from DB) --------------------------------
  const handleOpenReview = useCallback(async (review) => {
    try {
      const resp = await authFetch(`${API}/qa/reviews/${review.id}`);
      if (resp.ok) {
        const data = await resp.json();
        const videoData = {
          id: data.video_id,
          filename: data.video_filename,
          duration: data.video_duration,
          player_url: data.video_player_url,
          review_url: data.video_review_url,
        };

        // Issue 6c: Hydrate video detail (get video_blob_url for player)
        try {
          const detailResp = await authFetch(`${API}/detail/${data.video_id}`);
          if (detailResp.ok) {
            const detail = await detailResp.json();
            videoData.video_blob_url = detail.video_blob_url || detail.upload_blob_url;
            videoData.thumb_small = detail.thumb_small;
            videoData.thumb_large = detail.thumb_large;
            videoData.client_name = detail.client_name || '';
            videoData.project_number = detail.project_number || data.project_number || '';
            videoData.category = detail.category || '';
            videoData.variant = detail.variant || '';
            videoData.frameio_asset_id = detail.frameio_asset_id || '';
            if (detail.transcript_raw) setTranscript(detail.transcript_raw);
          }
        } catch { /* non-critical */ }

        setSelectedVideo(videoData);
        setQaResult(data.result_data);
        setQaFindings(data.findings || []);
        setCurrentReviewId(review.id);
        setQaSource(data.source || null);

        // Issue 6b: Restore qaOptions from stored checks_config and context_data
        const storedOptions = {};
        if (data.preset) storedOptions.preset = data.preset;
        if (data.strictness) storedOptions.strictness = data.strictness;
        if (data.checks_config) storedOptions.checks = data.checks_config;
        if (data.context_data) storedOptions.context = data.context_data;
        setQaOptions(Object.keys(storedOptions).length > 0 ? storedOptions : null);

        setQaError(null);
      }
    } catch { /* ignore */ }
  }, []);

  // Compute resolved/total from qaFindings for progress display
  const findingsResolved = qaFindings.filter(
    (f) => f.finding_status === 'Opgelost' || f.finding_status === 'Niet relevant'
  ).length;
  const findingsTotal = qaFindings.length;
  // Prefer persisted review status from DB, compute only as fallback
  const reviewStatus = qaResult?.review_status || (
    findingsTotal > 0 && findingsResolved >= findingsTotal ? 'Verwerkt'
    : findingsResolved > 0 ? 'In behandeling' : 'Nieuw'
  );
  const statusColorMap = { Nieuw: 'info', 'In behandeling': 'warning', Verwerkt: 'success' };

  // -- Render category sections in results ------------------------------------
  const renderCategorySection = (catKey, catData) => {
    const meta = CATEGORY_META[catKey] || { label: catKey, icon: '?', color: '#6b7280', bg: '#f9fafb' };
    const statusInfo = STATUS_LABELS[catData.status] || STATUS_LABELS.not_run;
    const findings = catData.findings || [];

    return (
      <Paper key={catKey} variant="outlined" sx={{ borderRadius: 2, mb: 1.5, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5,
          bgcolor: catData.status === 'completed' && findings.length > 0 ? meta.bg : '#fafafa',
        }}>
          <Typography sx={{ fontSize: '1.1rem' }}>{meta.icon}</Typography>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            {meta.label}
          </Typography>
          {catData.status === 'completed' && (
            <Chip
              label={findings.length === 0 ? 'Geen issues' : `${findings.length} finding${findings.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontWeight: 600, fontSize: '0.7rem', height: 22,
                bgcolor: findings.length === 0 ? '#dcfce7' : '#fef3c7',
                color: findings.length === 0 ? '#16a34a' : '#92400e',
              }}
            />
          )}
          {catData.status !== 'completed' && (
            <Chip
              label={statusInfo.label}
              size="small"
              sx={{ fontWeight: 500, fontSize: '0.7rem', height: 22, bgcolor: statusInfo.bg, color: statusInfo.color }}
            />
          )}
        </Box>
        {catData.status === 'not_implemented' && catData.message && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f1f5f9' }}>
            <Typography variant="caption" color="text.secondary">{catData.message}</Typography>
          </Box>
        )}
      </Paper>
    );
  };

  // -- Options panel view (pending video, waiting for user to configure) ------
  if (pendingVideo && !selectedVideo && !qaLoading) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3, pb: 6 }}>
        <Button startIcon={<ArrowBackIcon />}
          onClick={() => { setPendingVideo(null); }}
          sx={{ mb: 2, textTransform: 'none', color: '#6366f1' }}>
          Terug naar overzicht
        </Button>

        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3, px: 2.5, py: 2 }}>
          <Typography variant="h6" fontWeight={700}>{pendingVideo.filename}</Typography>
          <Typography variant="body2" color="text.secondary">
            Video is gereed voor analyse. Kies hieronder een check-profiel en start de analyse.
          </Typography>
        </Paper>

        <QAOptionsPanel onStart={handleOptionsStart} videoData={pendingVideo} projectData={pendingProjectData} />
      </Box>
    );
  }


  // -- Review view (video selected) -------------------------------------------
  if (selectedVideo) {
    const presetLabel = qaOptions?.preset && PRESETS[qaOptions.preset]
      ? `${PRESETS[qaOptions.preset].icon} ${PRESETS[qaOptions.preset].label}`
      : null;

    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3, pb: 6 }}>
        <Button startIcon={<ArrowBackIcon />}
          onClick={() => { setSelectedVideo(null); setQaResult(null); setQaError(null); setQaOptions(null); setCurrentReviewId(null); setQaFindings([]); setQaRunId(null); setQaSource(null); }}
          sx={{ mb: 2, textTransform: 'none', color: '#6366f1' }}>
          Terug naar overzicht
        </Button>

        {/* Video header */}
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
          {selectedVideo.video_blob_url && (
            <Box component="video" ref={videoRef} controls preload="metadata"
              poster={selectedVideo.thumb_large || selectedVideo.thumb_small || ''}
              sx={{ width: '100%', maxHeight: 300, bgcolor: '#000', display: 'block' }}>
              <source src={selectedVideo.video_blob_url} type="video/mp4" />
            </Box>
          )}
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="h6" fontWeight={700}>{selectedVideo.filename}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {selectedVideo.client_name && <Chip label={selectedVideo.client_name} size="small" variant="outlined" />}
              {selectedVideo.project_number && <Chip label={`#${selectedVideo.project_number}`} size="small" color="primary" />}
              {selectedVideo.duration > 0 && <Chip icon={<AccessTimeIcon />} label={formatTime(selectedVideo.duration)} size="small" />}
              {presetLabel && (
                <Chip label={presetLabel} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600 }} />
              )}
              {qaSource && (
                <Chip
                  label={qaSource === 'worker' ? 'Worker' : qaSource === 'azure' ? 'Azure' : qaSource === 'azure_fallback' ? 'Fallback' : qaSource === 'worker_fallback' ? 'Fallback' : qaSource}
                  size="small"
                  sx={{
                    fontSize: '0.65rem', height: 20, fontWeight: 600,
                    bgcolor: qaSource === 'worker' ? '#dbeafe' : qaSource === 'azure' ? '#fef3c7' : '#fce7f3',
                    color: qaSource === 'worker' ? '#1d4ed8' : qaSource === 'azure' ? '#92400e' : '#9d174d',
                  }}
                />
              )}
              {selectedVideo.player_url && (
                <Button size="small" startIcon={<PlayArrowIcon />}
                  onClick={() => window.open(selectedVideo.player_url, '_blank')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 'auto' }}>
                  Frame.io
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* QA loading */}
        {qaLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={36} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">Transcript analyseren op editfouten...</Typography>
            <Typography variant="caption" color="text.secondary">
              {qaElapsed < 30
                ? 'Heuristieken + LLM evaluatie...'
                : qaElapsed < 120
                  ? 'FFmpeg audio- en kleuranalyse loopt...'
                  : 'Nog even geduld, uitgebreide analyse...'}
              {' '}({qaElapsed}s)
            </Typography>
          </Box>
        )}

        {/* Transcript (show during loading and after results) */}
        {transcript && (
          <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
            <Box
              onClick={() => setTranscriptOpen((o) => !o)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5,
                cursor: 'pointer', '&:hover': { bgcolor: '#fafafa' }, transition: 'background 0.15s',
              }}
            >
              <TextSnippetIcon sx={{ color: '#6366f1', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                Transcript
              </Typography>
              <Chip label={`${(transcript || '').length.toLocaleString()} tekens`} size="small"
                sx={{ fontSize: '0.65rem', height: 20 }} />
              {transcript && !formattedTranscript && (
                <Button
                  size="small"
                  disabled={formattingTranscript}
                  onClick={(e) => { e.stopPropagation(); handleFormatTranscript(); }}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, px: 1 }}
                >
                  {formattingTranscript ? 'Bezig...' : 'Sprekers identificeren'}
                </Button>
              )}
              {formattedTranscript && (
                <Chip label="Sprekers" size="small" color="success"
                  sx={{ fontSize: '0.65rem', height: 20 }} />
              )}
              {transcriptOpen ? <ExpandLessIcon sx={{ color: '#94a3b8' }} /> : <ExpandMoreIcon sx={{ color: '#94a3b8' }} />}
            </Box>
            <Collapse in={transcriptOpen}>
              <Box sx={{
                px: 2.5, py: 2, maxHeight: 400, overflow: 'auto', borderTop: '1px solid #e5e7eb',
              }}>
                {(formattedTranscript || formatTranscriptForDisplay(transcript)).split(/\n\n+/).map((para, i) => (
                  <Typography key={i} variant="body2" sx={{
                    mb: 1.5, lineHeight: 1.8, color: '#374151',
                    fontSize: '0.85rem', fontFamily: '"Inter", "Segoe UI", sans-serif',
                  }}>
                    {para}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}

        {qaError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {qaError}
            <Button size="small" onClick={() => runQA(selectedVideo, qaOptions)} sx={{ ml: 2, textTransform: 'none' }}>Opnieuw</Button>
          </Alert>
        )}

        {qaResult && qaResult.summary && (
          <>
            {/* Summary */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {qaResult.summary.total === 0
                  ? 'Geen editfouten gevonden'
                  : `${qaResult.summary.total} mogelijke editfout${qaResult.summary.total !== 1 ? 'en' : ''}`}
              </Typography>
              {(qaResult.summary.by_severity?.high || qaResult.summary.blockers) > 0 && (
                <Chip label={`${qaResult.summary.by_severity?.high || qaResult.summary.blockers} hoog`} size="small" sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600 }} />
              )}
              {(qaResult.summary.by_severity?.medium || qaResult.summary.warnings) > 0 && (
                <Chip label={`${qaResult.summary.by_severity?.medium || qaResult.summary.warnings} medium`} size="small" sx={{ bgcolor: '#fffbeb', color: '#f59e0b', fontWeight: 600 }} />
              )}
              {(qaResult.summary.by_severity?.low || qaResult.summary.polish) > 0 && (
                <Chip label={`${qaResult.summary.by_severity?.low || qaResult.summary.polish} laag`} size="small" sx={{ bgcolor: '#f9fafb', color: '#6b7280' }} />
              )}
              <Box sx={{ flex: 1 }} />
              {currentReviewId && (
                <Tooltip title="Is de feedback op de vorige versie verwerkt? Uit Frame.io-comments, een mail of geplakte tekst.">
                  <Button size="small" variant="outlined" onClick={() => setFeedbackOpen(true)}
                    sx={{ textTransform: 'none', mr: 1, borderColor: '#dc2626', color: '#dc2626' }}>
                    🔁 Feedback gecontroleerd?
                  </Button>
                </Tooltip>
              )}
              {selectedVideo?.frameio_asset_id && qaResult?.summary?.total > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={pushingToFrameio ? <CircularProgress size={14} /> : <SendIcon />}
                  onClick={handlePushToFrameio}
                  disabled={pushingToFrameio}
                  sx={{ textTransform: 'none', mr: 1, borderColor: '#6366f1', color: '#6366f1' }}
                >
                  {pushingToFrameio ? 'Bezig...' : 'Push naar Frame.io'}
                </Button>
              )}
              <Button size="small" startIcon={<RefreshIcon />} onClick={() => {
                setPendingVideo(selectedVideo);
                setSelectedVideo(null);
                setQaResult(null);
                setQaError(null);
                setQaOptions(null);
                setCurrentReviewId(null);
                setQaFindings([]);
                setQaRunId(null);
                setQaSource(null);
                setPushToFrameioResult(null);
              }} sx={{ textTransform: 'none' }}>
                Opnieuw
              </Button>
            </Box>

            {pushToFrameioResult && (
              <Alert
                severity={pushToFrameioResult.success ? 'success' : 'error'}
                onClose={() => setPushToFrameioResult(null)}
                sx={{ mb: 2 }}
              >
                {pushToFrameioResult.message}
              </Alert>
            )}

            {qaResult.summary.total === 0 && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, mb: 2 }}>
                <Typography variant="body1" sx={{ color: '#10b981', fontWeight: 600 }}>Transcript ziet er schoon uit</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Geen herstarts, stiltes of productieresten gedetecteerd.
                </Typography>
              </Paper>
            )}

            {/* Review progress indicator */}
            {currentReviewId && findingsTotal > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip label={reviewStatus} color={statusColorMap[reviewStatus] || 'default'} size="small" />
                <Typography variant="body2" color="text.secondary">
                  {findingsResolved}/{findingsTotal} verwerkt
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(findingsResolved / findingsTotal) * 100}
                  sx={{
                    flex: 1, height: 6, borderRadius: 3,
                    bgcolor: '#e5e7eb',
                    '& .MuiLinearProgress-bar': { bgcolor: findingsResolved >= findingsTotal ? '#10b981' : '#6366f1', borderRadius: 3 },
                  }}
                />
              </Box>
            )}

            {/* Category sections (new) */}
            {qaResult.categories && (
              <Box sx={{ mb: 2 }}>
                {Object.entries(qaResult.categories).map(([catKey, catData]) =>
                  renderCategorySection(catKey, catData)
                )}
              </Box>
            )}

            <FeedbackCheckResultaat resultaat={qaResult?.feedback_check} onSeek={seekToTimestamp} />
            {/* Findings — use qaFindings (DB) if available, else qaResult.findings */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(qaFindings.length > 0 ? qaFindings : (qaResult.findings || [])).map((f, i) => {
                const fType = f.finding_type || f.type;
                const typeMeta = FINDING_TYPE_META[fType] || { label: fType, color: '#6b7280', icon: '?' };
                const sevMeta = SEVERITY_META[f.severity] || SEVERITY_META.medium;
                const fStatus = f.finding_status || 'Te doen';
                return (
                  <Paper key={f.id || i} variant="outlined" sx={{
                    borderRadius: 2, borderLeft: `4px solid ${typeMeta.color}`,
                    p: 2, '&:hover': { bgcolor: '#fafafa' },
                    opacity: fStatus === 'Opgelost' || fStatus === 'Niet relevant' ? 0.6 : 1,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '1.2rem', mt: 0.2 }}>{typeMeta.icon}</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mb: 0.75 }}>
                          <Typography variant="body1" fontWeight={700}>{typeMeta.label}</Typography>
                          <Chip label={f.timestamp_label} size="small" color="primary" variant="outlined"
                            onClick={() => seekToTimestamp(f.start_seconds)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem', height: 24, cursor: 'pointer' }} />
                          <Chip label={sevMeta.label} size="small"
                            sx={{ bgcolor: sevMeta.bg, color: sevMeta.color, fontWeight: 600, height: 22 }} />
                          {f.confidence >= 0.5 && (
                            <Tooltip title={`Zekerheid: ${Math.round(f.confidence * 100)}%`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LinearProgress variant="determinate" value={f.confidence * 100}
                                  sx={{ width: 50, height: 5, borderRadius: 2, bgcolor: '#e5e7eb',
                                    '& .MuiLinearProgress-bar': { bgcolor: f.confidence > 0.8 ? '#10b981' : f.confidence > 0.6 ? '#f59e0b' : '#94a3b8' } }} />
                                <Typography variant="caption" color="text.secondary">{Math.round(f.confidence * 100)}%</Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        {f.reason && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, lineHeight: 1.5 }}>{f.reason}</Typography>
                        )}
                        {f.evidence_text && (
                          <Box sx={{
                            bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0',
                            px: 1.5, py: 1, fontFamily: 'monospace', fontSize: '0.8rem',
                            color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                          }}>
                            {f.evidence_text}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        {f.suggested_action && f.suggested_action !== 'not_an_issue' && (
                          <Chip label={ACTION_LABELS[f.suggested_action] || f.suggested_action} size="small"
                            sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600, fontSize: '0.7rem' }} />
                        )}
                        {f.id && (
                          <Select
                            size="small"
                            value={fStatus}
                            onChange={(e) => handleFindingStatusChange(f.id, e.target.value)}
                            sx={{ minWidth: 130, height: 28, fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.25 } }}
                          >
                            <MenuItem value="Te doen" sx={{ fontSize: '0.8rem' }}>Te doen</MenuItem>
                            <MenuItem value="Opgepakt" sx={{ fontSize: '0.8rem' }}>Opgepakt</MenuItem>
                            <MenuItem value="Opgelost" sx={{ fontSize: '0.8rem' }}>Opgelost</MenuItem>
                            <MenuItem value="Niet relevant" sx={{ fontSize: '0.8rem' }}>Niet relevant</MenuItem>
                          </Select>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    );
  }

  // -- Video selection + upload view ------------------------------------------
  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="bug_report"
        title="Video Edit QA"
        subtitle="Upload een video of selecteer uit eerder geanalyseerde video's — we checken automatisch op editfouten"
        accent={{ color: '#635983', bg: '#dbcdff' }}
      />

      {/* Upload zone */}
      <Paper
        variant="outlined"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
        sx={{
          borderRadius: 2, p: 3, mb: 3, textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
          borderStyle: 'dashed', borderWidth: 2,
          borderColor: dragOver ? '#6366f1' : '#d1d5db',
          bgcolor: dragOver ? '#f5f3ff' : uploading ? '#f9fafb' : '#fff',
          transition: 'all 0.2s',
          '&:hover': uploading ? {} : { borderColor: '#6366f1', bgcolor: '#faf5ff' },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp4,.mov,.mkv,.avi,.webm"
          hidden
          onChange={(e) => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); e.target.value = ''; }}
        />

        {uploading ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Stepper activeStep={uploadStep} alternativeLabel sx={{
              '& .MuiStepLabel-label': { fontSize: '0.75rem' },
              '& .MuiStepIcon-root': { fontSize: '1.2rem' },
              '& .MuiStepIcon-root.Mui-active': { color: '#6366f1' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#10b981' },
            }}>
              {PROCESS_STEPS.map((s) => (
                <Step key={s.label}><StepLabel>{s.label}</StepLabel></Step>
              ))}
            </Stepper>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
              {PROCESS_STEPS[Math.min(uploadStep, PROCESS_STEPS.length - 1)]?.desc}
            </Typography>
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 40, color: '#6366f1', mb: 1, opacity: 0.6 }} />
            <Typography variant="body1" fontWeight={600}>
              Sleep een video hierheen of klik om te uploaden
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MP4, MOV, MKV, AVI, WebM — wordt automatisch getranscribeerd en gecheckt
            </Typography>
          </Box>
        )}

        {uploadError && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{uploadError}</Alert>
        )}
      </Paper>

      {/* Frame.io URL input */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth size="small"
            placeholder="Plak een Frame.io link (player of review URL)..."
            value={frameioUrl}
            onChange={(e) => setFrameioUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleFrameioSubmit(); }}
            disabled={frameioLoading}
            InputProps={{
              startAdornment: <InputAdornment position="start"><PlayArrowIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment>,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Button
            variant="contained"
            onClick={handleFrameioSubmit}
            disabled={!frameioUrl.trim() || frameioLoading}
            sx={{ textTransform: 'none', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, minWidth: 100, borderRadius: 2 }}
          >
            {frameioLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Check'}
          </Button>
        </Box>
        {frameioLoading && (
          <Paper variant="outlined" sx={{ mt: 1.5, p: 2, borderRadius: 2 }}>
            <Stepper activeStep={frameioStep} alternativeLabel sx={{
              '& .MuiStepLabel-label': { fontSize: '0.75rem' },
              '& .MuiStepIcon-root': { fontSize: '1.2rem' },
              '& .MuiStepIcon-root.Mui-active': { color: '#6366f1' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#10b981' },
            }}>
              {PROCESS_STEPS.map((s) => (
                <Step key={s.label}><StepLabel>{s.label}</StepLabel></Step>
              ))}
            </Stepper>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              {PROCESS_STEPS[Math.min(frameioStep, PROCESS_STEPS.length - 1)]?.desc}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Recent QA reviews (from database) */}
      {qaHistory.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
            Recente QA reviews:
          </Typography>
          <Grid container spacing={1.5}>
            {qaHistory.map((h) => {
              const hResolved = h.findings_resolved || 0;
              const hTotal = h.findings_total || h.total_findings || 0;
              const hStatus = h.review_status || 'Nieuw';
              return (
                <Grid item xs={12} sm={6} key={h.id}>
                  <Paper variant="outlined" onClick={() => handleOpenReview(h)} sx={{
                    borderRadius: 2, p: 2, cursor: 'pointer', display: 'flex', gap: 1.5,
                    '&:hover': { borderColor: '#6366f1', bgcolor: '#faf5ff' }, transition: 'all 0.15s',
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{h.video_filename || h.filename}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          label={hTotal === 0 ? 'Schoon' : `${hTotal} finding${hTotal !== 1 ? 's' : ''}${h.blockers > 0 ? ` (${h.blockers} hoog)` : ''}`}
                          size="small"
                          sx={{
                            fontSize: '0.65rem', height: 20, fontWeight: 600,
                            bgcolor: hTotal === 0 ? '#dcfce7' : h.blockers > 0 ? '#fef2f2' : '#fef3c7',
                            color: hTotal === 0 ? '#16a34a' : h.blockers > 0 ? '#ef4444' : '#92400e',
                          }}
                        />
                        <Chip
                          label={hStatus}
                          size="small"
                          sx={{
                            fontSize: '0.6rem', height: 18, fontWeight: 600,
                            bgcolor: hStatus === 'Verwerkt' ? '#dcfce7' : hStatus === 'In behandeling' ? '#fffbeb' : '#f1f5f9',
                            color: hStatus === 'Verwerkt' ? '#16a34a' : hStatus === 'In behandeling' ? '#b45309' : '#64748b',
                          }}
                        />
                      </Box>
                      {hTotal > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={(hResolved / hTotal) * 100}
                          sx={{
                            mt: 0.75, height: 4, borderRadius: 2, bgcolor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': { bgcolor: hResolved >= hTotal ? '#10b981' : '#6366f1', borderRadius: 2 },
                          }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                        {h.created_by && h.created_by !== 'system' ? h.created_by.split('@')[0] : ''}{' '}
                        {h.created_at ? new Date(h.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </Typography>
                    </Box>
                    <BugReportIcon sx={{ color: '#6366f1', opacity: 0.4, alignSelf: 'center' }} />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
      <FeedbackCheckDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} reviewId={currentReviewId}
        userEmail={userEmail || ''} onKlaar={() => { if (currentReviewId) handleOpenReview({ id: currentReviewId }); }} />
    </Box>
  );
}
